import { useEffect, useMemo, useRef, useState } from 'react';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import FormattedDateInput from '../../components/ui/FormattedDateInput';
import WizardSelect from '../../components/ui/WizardSelect';
import {
  ReviewChips,
  ReviewField,
  ReviewGrid,
  ReviewSection,
  ReviewSubBlock,
} from '../../components/criminal/CriminalReviewUi';
import {
  getExtraditionMlaRequestViewData,
  saveExtraditionMlaRequest,
  type ExtraditionMlaRequestViewData,
} from '../../services/extraditionMlaCaseRequests';
import type { ExtraditionMlaWizardDraft } from '../../types/extraditionMlaCaseRequest';
import type { CriminalWizardDraft } from '../../types/criminalCaseRequest';
import {
  calcAgeFromDob,
  sliceMax,
  todayIsoDateLocal,
} from '../../utils/caseFieldValidation';
import { formatDisplayDateTime } from '../../utils/dateFormat';
import { setFlashToast } from '../../utils/flashToast';
import { focusFirstInvalid } from '../../utils/requiredValidation';
import { apiDisplayValue, deliveryMethodDisplay } from '../view-request/viewRequestHelpers';
import type { ExtraditionMlaWizardStep } from '../wizard/extraditionMla/extraditionMlaConstants';
import {
  isHighUrgencyLevel,
  requiresCustodialAuthority,
} from '../wizard/extraditionMla/extraditionMlaHelpers';
import {
  minDeadlineDateForExtraditionMla,
  validateExtraditionMlaStep,
} from '../wizard/extraditionMla/extraditionMlaStepValidation';
import { useExtraditionMlaLookups } from '../wizard/extraditionMla/useExtraditionMlaLookups';
import { FieldError, inpClass } from './criminalFieldUi';
import {
  confirmCaseUpdateCancel,
  formatSubmittedAtLabel,
  isCaseRequestUuid,
  mergeSavedStepIntoInitialBaseline,
  toSelectOpts,
} from './taskCaseUpdateHelpers';
import TaskCaseUpdateSectionActions from './TaskCaseUpdateSectionActions';

type Props = {
  caseRequestId: string;
  recentSubmittedAt: string;
  readOnly?: boolean;
  viewData?: ExtraditionMlaRequestViewData | null;
  viewDataLoading?: boolean;
  onCancelConfirmed?: () => void;
};

const MAX_DESC = 2000;
const MAX_REF = 255;
const MAX_TEXT = 2000;

type EditableStep = 1 | 2 | 3 | 4;

function cloneDraft(d: ExtraditionMlaWizardDraft): ExtraditionMlaWizardDraft {
  return {
    ...d,
    applicableAreas: [...d.applicableAreas],
    applicableAreaIds: [...d.applicableAreaIds],
    deliveryMethodIds: [...d.deliveryMethodIds],
    deliveryMethods: [...d.deliveryMethods],
    documents: d.documents.map((doc) => ({ ...doc })),
  };
}

function draftHasUnsavedChanges(
  current: ExtraditionMlaWizardDraft,
  saved: ExtraditionMlaWizardDraft,
): boolean {
  return JSON.stringify(current) !== JSON.stringify(saved);
}

function pickStep1Snapshot(d: ExtraditionMlaWizardDraft) {
  return {
    description: d.description,
    caseSource: d.caseSource,
    countryInvolvedId: d.countryInvolvedId,
    directionOfRequest: d.directionOfRequest,
    provinceId: d.provinceId,
    districtId: d.districtId,
    yourReferenceNumber: d.yourReferenceNumber,
    otherExternalReferences: d.otherExternalReferences,
    caseNumber: d.caseNumber,
    hasConnectedMatters: d.hasConnectedMatters,
    connectedCaseNumber: d.connectedCaseNumber,
    connectedAgdFileRefAvailable: d.connectedAgdFileRefAvailable,
    connectedAgdFileRef: d.connectedAgdFileRef,
  };
}

function pickStep2Snapshot(d: ExtraditionMlaWizardDraft) {
  return {
    accusedName: d.accusedName,
    nationalityId: d.nationalityId,
    countryOfOriginId: d.countryOfOriginId,
    accusedDob: d.accusedDob,
    accusedGenderId: d.accusedGenderId,
    passportOrIdNumber: d.passportOrIdNumber,
    custodyStatusId: d.custodyStatusId,
    custodialAuthority: d.custodialAuthority,
    lastKnownLocation: d.lastKnownLocation,
  };
}

function pickStep3Snapshot(d: ExtraditionMlaWizardDraft) {
  return {
    arrestWarrantAvailable: d.arrestWarrantAvailable,
    warrantReference: d.warrantReference,
    applicableTreatyArrangement: d.applicableTreatyArrangement,
  };
}

function pickStep4Snapshot(d: ExtraditionMlaWizardDraft) {
  return {
    urgencyLevelId: d.urgencyLevelId,
    deadline: d.deadline,
    urgencyJustification: d.urgencyJustification,
  };
}

function stepHasData(step: EditableStep, d: ExtraditionMlaWizardDraft): boolean {
  if (step === 1) {
    return !!(
      d.description.trim() ||
      d.caseSource.trim() ||
      d.yourReferenceNumber.trim() ||
      d.provinceId.trim() ||
      d.caseNumber.trim()
    );
  }
  if (step === 2) {
    return !!(d.accusedName.trim() || d.nationalityId.trim() || d.accusedDob.trim());
  }
  if (step === 3) {
    return !!(
      d.arrestWarrantAvailable ||
      d.warrantReference.trim() ||
      d.applicableTreatyArrangement.trim()
    );
  }
  return !!(d.urgencyLevelId.trim() || d.deadline.trim() || d.urgencyJustification.trim());
}

function canSaveStep(
  step: EditableStep,
  draft: ExtraditionMlaWizardDraft | null,
  initial: ExtraditionMlaWizardDraft | null,
): boolean {
  if (!draft || !initial) return false;
  const pickers = {
    1: pickStep1Snapshot,
    2: pickStep2Snapshot,
    3: pickStep3Snapshot,
    4: pickStep4Snapshot,
  } as const;
  const changed = JSON.stringify(pickers[step](draft)) !== JSON.stringify(pickers[step](initial));
  return (stepHasData(step, draft) || stepHasData(step, initial)) && changed;
}

function mergeLookupItems(
  items: { id: string; name: string; code?: string }[],
  selectedId: string,
  selectedName: string,
): { id: string; name: string; code?: string }[] {
  if (!selectedId || items.some((x) => x.id === selectedId)) return items;
  return [...items, { id: selectedId, name: selectedName || selectedId }];
}

export default function TaskCaseExtraditionMlaUpdateTab({
  caseRequestId,
  recentSubmittedAt,
  readOnly = false,
  viewData,
  viewDataLoading = false,
  onCancelConfirmed,
}: Props) {
  const [draft, setDraft] = useState<ExtraditionMlaWizardDraft | null>(null);
  const [initial, setInitial] = useState<ExtraditionMlaWizardDraft | null>(null);
  const [submittedLabel, setSubmittedLabel] = useState(recentSubmittedAt);
  const [loadedForKey, setLoadedForKey] = useState<string | null>(null);
  const [loadError, setLoadError] = useState('');
  const [savingStep, setSavingStep] = useState<ExtraditionMlaWizardStep | null>(null);
  const saveInFlightRef = useRef(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const isSaving = savingStep !== null;

  const lookups = useExtraditionMlaLookups('review', draft?.provinceId ?? '');
  const minDeadlineDate = useMemo(() => minDeadlineDateForExtraditionMla(), []);
  const maxDob = useMemo(() => todayIsoDateLocal(), []);
  const useSharedViewData = viewData !== undefined || viewDataLoading;
  const loadKey = `${caseRequestId}|${recentSubmittedAt}|${viewData?.submittedAt ?? ''}|${viewData?.draft.draftRequestId ?? ''}`;
  const loading = useSharedViewData
    ? viewDataLoading || loadedForKey !== loadKey
    : loadedForKey !== loadKey;

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!isCaseRequestUuid(caseRequestId)) {
        if (cancelled) return;
        setDraft(null);
        setInitial(null);
        setLoadError('Case request id is missing or invalid.');
        setLoadedForKey(loadKey);
        return;
      }

      if (useSharedViewData) {
        if (viewDataLoading) return;
        if (!viewData?.draft) {
          if (cancelled) return;
          setDraft(null);
          setInitial(null);
          setLoadError('Could not load case update data.');
          setLoadedForKey(loadKey);
          return;
        }
        if (cancelled) return;
        const loaded = cloneDraft(viewData.draft);
        setDraft(loaded);
        setInitial(cloneDraft(loaded));
        setSubmittedLabel(formatSubmittedAtLabel(viewData.submittedAt, recentSubmittedAt));
        setLoadError('');
        setLoadedForKey(loadKey);
        return;
      }

      const res = await getExtraditionMlaRequestViewData(caseRequestId);
      if (cancelled) return;
      if (!res.success || !res.data?.draft) {
        setDraft(null);
        setInitial(null);
        setLoadError((!res.success ? res.message : undefined) ?? 'Could not load case update data.');
        setLoadedForKey(loadKey);
        return;
      }
      const loaded = cloneDraft(res.data.draft);
      setDraft(loaded);
      setInitial(cloneDraft(loaded));
      setSubmittedLabel(formatSubmittedAtLabel(res.data.submittedAt, recentSubmittedAt));
      setLoadError('');
      setLoadedForKey(loadKey);
    })();
    return () => {
      cancelled = true;
    };
  }, [caseRequestId, loadKey, recentSubmittedAt, useSharedViewData, viewData, viewDataLoading]);

  const canSaveStep1 = useMemo(() => canSaveStep(1, draft, initial), [draft, initial]);
  const canSaveStep2 = useMemo(() => canSaveStep(2, draft, initial), [draft, initial]);
  const canSaveStep3 = useMemo(() => canSaveStep(3, draft, initial), [draft, initial]);
  const canSaveStep4 = useMemo(() => canSaveStep(4, draft, initial), [draft, initial]);
  const clearErr = (key: string) => setErrors((e) => ({ ...e, [key]: '' }));

  const saveSection = async (step: EditableStep) => {
    if (!draft || readOnly || saveInFlightRef.current) return;
    const result = validateExtraditionMlaStep(step, draft);
    setErrors(result.errors);
    if (!result.valid) {
      focusFirstInvalid(result.specs, result.errors);
      return;
    }

    saveInFlightRef.current = true;
    setSavingStep(step);
    try {
      const r = await saveExtraditionMlaRequest(draft, {
        isDraft: false,
        confirmedCorrect: true,
        currentStep: step,
        isUpdateAlreadySubmitted: true,
      });
      if (!r.success) {
        setFlashToast({ message: r.message ?? 'Failed to save case update.', variant: 'error' });
        return;
      }
      const next = {
        ...draft,
        draftRequestId: r.data.requestId || draft.draftRequestId,
        referenceNo: r.data.fileReferenceNumber ?? draft.referenceNo,
      };
      setDraft(next);
      setInitial((prev) => {
        if (!prev) return cloneDraft(next);
        const pickers = {
          1: pickStep1Snapshot,
          2: pickStep2Snapshot,
          3: pickStep3Snapshot,
          4: pickStep4Snapshot,
        } as const;
        return mergeSavedStepIntoInitialBaseline(prev, next, pickers[step], cloneDraft);
      });
      setErrors({});
      setFlashToast({ message: 'Case update saved successfully.', variant: 'success' });
    } finally {
      saveInFlightRef.current = false;
      setSavingStep(null);
    }
  };

  const resetSection = () => {
    if (initial) {
      setDraft(cloneDraft(initial));
      setErrors({});
    }
  };

  const beginCancel = () => {
    if (isSaving || !draft || !initial) return;
    if (draftHasUnsavedChanges(draft, initial)) {
      setCancelConfirmOpen(true);
      return;
    }
    resetSection();
  };

  if (loading) return <p className="tcd-update-ts">Loading case update…</p>;
  if (!draft) {
    return (
      <div className="tcd-case-update-error" role="alert">
        <p className="tcd-update-ts" style={{ color: 'var(--red)', margin: 0 }}>
          {loadError || 'Could not load case update data.'}
        </p>
      </div>
    );
  }

  const accusedAge = calcAgeFromDob(draft.accusedDob);
  const custodyStatusOptions = mergeLookupItems(
    lookups.custodyStatuses,
    draft.custodyStatusId,
    draft.custodyStatusName,
  );
  const selectedCustodyStatus = custodyStatusOptions.find((s) => s.id === draft.custodyStatusId);
  const showCustodialAuthority = requiresCustodialAuthority(
    draft.custodyStatusName,
    selectedCustodyStatus?.code,
  );
  const urgencyJustificationRequired = isHighUrgencyLevel(draft.urgencyLevelName);
  const deliveryLabel = deliveryMethodDisplay(
    draft as unknown as CriminalWizardDraft,
    lookups.deliveryMethods,
  );
  const nationalityItems = lookups.nationalities.length ? lookups.nationalities : lookups.countries;

  return (
    <>
      <ConfirmDialog
        isOpen={cancelConfirmOpen}
        title="Cancel Changes"
        message="Are you sure you want to cancel? Any unsaved changes will be lost."
        onClose={() => setCancelConfirmOpen(false)}
        onConfirm={() => {
          confirmCaseUpdateCancel(resetSection, () => setCancelConfirmOpen(false), onCancelConfirmed);
        }}
      />

      <div className="tcd-case-update">
        <p className="tcd-update-ts">
          Request Submitted at:{' '}
          <strong>{submittedLabel || formatDisplayDateTime(recentSubmittedAt) || '—'}</strong>
        </p>

        <ReviewSection step="Step 01" title="Case Overview">
          <ReviewSubBlock title="Case Categorization">
            <ReviewGrid cols={4}>
              <ReviewField label="File Category" value={draft.fileCategory} />
              <ReviewField label="Sub File Category" value={draft.subFileCategoryName} />
            </ReviewGrid>
            <ReviewChips items={draft.applicableAreas} />
          </ReviewSubBlock>

          <ReviewSubBlock title="Case Intake Details">
            <div className="ccr-fg" style={{ marginBottom: 14 }}>
              <label>
                Description <span className="req">*</span>
              </label>
              <textarea
                id="emla-description"
                className={inpClass(Boolean(errors.description))}
                rows={4}
                maxLength={MAX_DESC}
                value={draft.description}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  setDraft((d) => (d ? { ...d, description: sliceMax(e.target.value, MAX_DESC) } : d));
                  clearErr('description');
                }}
              />
              <FieldError message={errors.description} />
            </div>

            <div className="ccr-fg" style={{ marginBottom: 14 }}>
              <label>
                Case Source <span className="req">*</span>
              </label>
              <textarea
                id="emla-caseSource"
                className={inpClass(Boolean(errors.caseSource))}
                rows={3}
                maxLength={MAX_DESC}
                value={draft.caseSource}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  setDraft((d) => (d ? { ...d, caseSource: sliceMax(e.target.value, MAX_DESC) } : d));
                  clearErr('caseSource');
                }}
              />
              <FieldError message={errors.caseSource} />
            </div>

            <div className="ccr-fr c2">
              <div className="ccr-fg">
                <label>
                  Country Involved <span className="req">*</span>
                </label>
                <WizardSelect
                  id="emla-countryInvolved"
                  hasError={Boolean(errors.countryInvolvedId)}
                  value={draft.countryInvolvedId}
                  disabled={readOnly}
                  placeholder="Select Country"
                  options={toSelectOpts(
                    mergeLookupItems(
                      lookups.countries,
                      draft.countryInvolvedId,
                      draft.countryInvolvedName,
                    ),
                  )}
                  onChange={(id) => {
                    const name = lookups.countries.find((c) => c.id === id)?.name ?? '';
                    setDraft((d) =>
                      d ? { ...d, countryInvolvedId: id, countryInvolvedName: name } : d,
                    );
                    clearErr('countryInvolvedId');
                  }}
                />
                <FieldError message={errors.countryInvolvedId} />
              </div>
              <div className="ccr-fg">
                <label>Direction of Request</label>
                <input
                  className={inpClass(false)}
                  value={draft.directionOfRequest}
                  readOnly={readOnly}
                  disabled={readOnly}
                  maxLength={MAX_REF}
                  onChange={(e) =>
                    setDraft((d) =>
                      d ? { ...d, directionOfRequest: sliceMax(e.target.value, MAX_REF) } : d,
                    )
                  }
                />
              </div>
              <div className="ccr-fg">
                <label>
                  Province <span className="req">*</span>
                </label>
                <WizardSelect
                  id="emla-province"
                  hasError={Boolean(errors.provinceId)}
                  value={draft.provinceId}
                  disabled={readOnly}
                  placeholder="Select Province"
                  options={toSelectOpts(
                    mergeLookupItems(lookups.provinces, draft.provinceId, draft.provinceName),
                  )}
                  onChange={(id) => {
                    const name = lookups.provinces.find((p) => p.id === id)?.name ?? '';
                    setDraft((d) =>
                      d
                        ? {
                            ...d,
                            provinceId: id,
                            provinceName: name,
                            districtId: '',
                            districtName: '',
                          }
                        : d,
                    );
                    clearErr('provinceId');
                  }}
                />
                <FieldError message={errors.provinceId} />
              </div>
              <div className="ccr-fg">
                <label>
                  District <span className="req">*</span>
                </label>
                <WizardSelect
                  id="emla-district"
                  hasError={Boolean(errors.districtId)}
                  value={draft.districtId}
                  disabled={readOnly || !draft.provinceId}
                  placeholder={draft.provinceId ? 'Select District' : 'Select province first'}
                  options={toSelectOpts(
                    mergeLookupItems(lookups.districts, draft.districtId, draft.districtName),
                  )}
                  onChange={(id) => {
                    const name = lookups.districts.find((x) => x.id === id)?.name ?? '';
                    setDraft((d) => (d ? { ...d, districtId: id, districtName: name } : d));
                    clearErr('districtId');
                  }}
                />
                <FieldError message={errors.districtId} />
              </div>
              <div className="ccr-fg">
                <label>
                  Your Reference Number (External) <span className="req">*</span>
                </label>
                <input
                  id="emla-yourRef"
                  className={inpClass(Boolean(errors.yourReferenceNumber))}
                  value={draft.yourReferenceNumber}
                  readOnly={readOnly}
                  disabled={readOnly}
                  maxLength={MAX_REF}
                  onChange={(e) => {
                    setDraft((d) =>
                      d ? { ...d, yourReferenceNumber: sliceMax(e.target.value, MAX_REF) } : d,
                    );
                    clearErr('yourReferenceNumber');
                  }}
                />
                <FieldError message={errors.yourReferenceNumber} />
              </div>
              <div className="ccr-fg">
                <label>Other External Reference Number(s)</label>
                <input
                  className={inpClass(false)}
                  value={draft.otherExternalReferences}
                  readOnly={readOnly}
                  disabled={readOnly}
                  maxLength={MAX_REF}
                  onChange={(e) =>
                    setDraft((d) =>
                      d ? { ...d, otherExternalReferences: sliceMax(e.target.value, MAX_REF) } : d,
                    )
                  }
                />
              </div>
              <div className="ccr-fg">
                <label>Case Number</label>
                <input
                  className={inpClass(false)}
                  value={draft.caseNumber}
                  readOnly={readOnly}
                  disabled={readOnly}
                  maxLength={MAX_REF}
                  onChange={(e) =>
                    setDraft((d) => (d ? { ...d, caseNumber: sliceMax(e.target.value, MAX_REF) } : d))
                  }
                />
              </div>
              <div className="ccr-fg" id="emla-connected">
                <label>
                  Connected matters <span className="req">*</span>
                </label>
                <div className="ccr-radio-row">
                  {(['yes', 'no'] as const).map((v) => (
                    <label key={v} className="ccr-radio">
                      <input
                        type="radio"
                        name="emla-connected"
                        checked={draft.hasConnectedMatters === v}
                        disabled={readOnly}
                        onChange={() => {
                          setDraft((d) =>
                            d
                              ? {
                                  ...d,
                                  hasConnectedMatters: v,
                                  ...(v === 'no'
                                    ? {
                                        connectedCaseNumber: '',
                                        connectedAgdFileRefAvailable: false,
                                        connectedAgdFileRef: '',
                                      }
                                    : {}),
                                }
                              : d,
                          );
                          clearErr('hasConnectedMatters');
                        }}
                      />
                      {v === 'yes' ? 'Yes' : 'No'}
                    </label>
                  ))}
                </div>
                <FieldError message={errors.hasConnectedMatters} />
              </div>
              {draft.hasConnectedMatters === 'yes' ? (
                <>
                  <div className="ccr-fg">
                    <label>
                      Connected Case Number <span className="req">*</span>
                    </label>
                    <input
                      id="emla-connectedCase"
                      className={inpClass(Boolean(errors.connectedCaseNumber))}
                      value={draft.connectedCaseNumber}
                      readOnly={readOnly}
                      disabled={readOnly}
                      maxLength={MAX_REF}
                      onChange={(e) => {
                        setDraft((d) =>
                          d ? { ...d, connectedCaseNumber: sliceMax(e.target.value, MAX_REF) } : d,
                        );
                        clearErr('connectedCaseNumber');
                      }}
                    />
                    <FieldError message={errors.connectedCaseNumber} />
                  </div>
                  <div className="ccr-fg">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <label style={{ marginBottom: 0 }}>
                        Connected AGD File Reference Number{' '}
                        {draft.connectedAgdFileRefAvailable ? <span className="req">*</span> : null}
                      </label>
                      <input
                        type="checkbox"
                        style={{ width: 15, height: 15, accentColor: '#000', cursor: 'pointer' }}
                        checked={draft.connectedAgdFileRefAvailable}
                        disabled={readOnly}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setDraft((d) =>
                            d
                              ? {
                                  ...d,
                                  connectedAgdFileRefAvailable: checked,
                                  connectedAgdFileRef: checked ? d.connectedAgdFileRef : '',
                                }
                              : d,
                          );
                          clearErr('connectedAgdFileRef');
                        }}
                      />
                      <span>Available</span>
                    </div>
                    <input
                      id="emla-connectedAgd"
                      className={inpClass(Boolean(errors.connectedAgdFileRef))}
                      value={draft.connectedAgdFileRef}
                      readOnly={readOnly || !draft.connectedAgdFileRefAvailable}
                      disabled={readOnly || !draft.connectedAgdFileRefAvailable}
                      maxLength={MAX_REF}
                      onChange={(e) => {
                        setDraft((d) =>
                          d ? { ...d, connectedAgdFileRef: sliceMax(e.target.value, MAX_REF) } : d,
                        );
                        clearErr('connectedAgdFileRef');
                      }}
                    />
                    <FieldError message={errors.connectedAgdFileRef} />
                  </div>
                </>
              ) : null}
            </div>
          </ReviewSubBlock>

          <TaskCaseUpdateSectionActions
            canSave={canSaveStep1}
            saving={savingStep === 1}
            readOnly={readOnly}
            onSave={() => void saveSection(1)}
            onCancel={beginCancel}
          />
        </ReviewSection>

        <ReviewSection step="Step 02" title="Accused Person Details">
          <ReviewSubBlock title="Accused Person Details">
            <div className="ccr-fg" style={{ marginBottom: 14 }}>
              <label>
                Name of the Accused <span className="req">*</span>
              </label>
              <input
                id="emla-accusedName"
                className={inpClass(Boolean(errors.accusedName))}
                value={draft.accusedName}
                readOnly={readOnly}
                disabled={readOnly}
                maxLength={MAX_REF}
                onChange={(e) => {
                  setDraft((d) => (d ? { ...d, accusedName: sliceMax(e.target.value, MAX_REF) } : d));
                  clearErr('accusedName');
                }}
              />
              <FieldError message={errors.accusedName} />
            </div>

            <div className="ccr-fr c2">
              <div className="ccr-fg">
                <label>
                  Nationality <span className="req">*</span>
                </label>
                <WizardSelect
                  id="emla-nationality"
                  hasError={Boolean(errors.nationalityId)}
                  value={draft.nationalityId}
                  disabled={readOnly}
                  placeholder="Select Nationality"
                  options={toSelectOpts(
                    mergeLookupItems(nationalityItems, draft.nationalityId, draft.nationalityName),
                  )}
                  onChange={(id) => {
                    const name = nationalityItems.find((x) => x.id === id)?.name ?? '';
                    setDraft((d) => (d ? { ...d, nationalityId: id, nationalityName: name } : d));
                    clearErr('nationalityId');
                  }}
                />
                <FieldError message={errors.nationalityId} />
              </div>
              <div className="ccr-fg">
                <label>
                  Country of Origin <span className="req">*</span>
                </label>
                <WizardSelect
                  id="emla-countryOfOrigin"
                  hasError={Boolean(errors.countryOfOriginId)}
                  value={draft.countryOfOriginId}
                  disabled={readOnly}
                  placeholder="Select Country of Origin"
                  options={toSelectOpts(
                    mergeLookupItems(
                      lookups.countries,
                      draft.countryOfOriginId,
                      draft.countryOfOriginName,
                    ),
                  )}
                  onChange={(id) => {
                    const name = lookups.countries.find((x) => x.id === id)?.name ?? '';
                    setDraft((d) =>
                      d ? { ...d, countryOfOriginId: id, countryOfOriginName: name } : d,
                    );
                    clearErr('countryOfOriginId');
                  }}
                />
                <FieldError message={errors.countryOfOriginId} />
              </div>
              <div className="ccr-fg">
                <label>
                  Date of Birth <span className="req">*</span>
                </label>
                <FormattedDateInput
                  id="emla-accusedDob"
                  className={inpClass(Boolean(errors.accusedDob))}
                  value={draft.accusedDob}
                  maxDate={maxDob}
                  enableYmdViews
                  disabled={readOnly}
                  onChange={(next) => {
                    setDraft((d) => (d ? { ...d, accusedDob: next } : d));
                    clearErr('accusedDob');
                  }}
                />
                <FieldError message={errors.accusedDob} />
              </div>
              <div className="ccr-fg">
                <label>Age</label>
                <div className="ccr-inp ccr-inp--static" aria-live="polite">
                  {typeof accusedAge === 'number' ? String(accusedAge) : '—'}
                </div>
              </div>
              <div className="ccr-fg">
                <label>
                  Gender <span className="req">*</span>
                </label>
                <WizardSelect
                  id="emla-accusedGender"
                  hasError={Boolean(errors.accusedGenderId)}
                  value={draft.accusedGenderId}
                  disabled={readOnly}
                  placeholder="Select Gender"
                  options={toSelectOpts(lookups.genders)}
                  onChange={(id) => {
                    const name = lookups.genders.find((x) => x.id === id)?.name ?? '';
                    setDraft((d) =>
                      d ? { ...d, accusedGenderId: id, accusedGenderName: name } : d,
                    );
                    clearErr('accusedGenderId');
                  }}
                />
                <FieldError message={errors.accusedGenderId} />
              </div>
              <div className="ccr-fg">
                <label>Passport/ID Number</label>
                <input
                  className={inpClass(false)}
                  value={draft.passportOrIdNumber}
                  readOnly={readOnly}
                  disabled={readOnly}
                  maxLength={MAX_REF}
                  onChange={(e) =>
                    setDraft((d) =>
                      d ? { ...d, passportOrIdNumber: sliceMax(e.target.value, MAX_REF) } : d,
                    )
                  }
                />
              </div>
              <div className="ccr-fg">
                <label>
                  Current Custody Status <span className="req">*</span>
                </label>
                <WizardSelect
                  id="emla-custodyStatus"
                  hasError={Boolean(errors.custodyStatusId)}
                  value={draft.custodyStatusId}
                  disabled={readOnly}
                  placeholder="Select Custody Status"
                  options={toSelectOpts(custodyStatusOptions)}
                  onChange={(id) => {
                    const item = custodyStatusOptions.find((x) => x.id === id);
                    const name = item?.name ?? '';
                    const code = item?.code ?? '';
                    setDraft((d) =>
                      d
                        ? {
                            ...d,
                            custodyStatusId: id,
                            custodyStatusName: name,
                            ...(!requiresCustodialAuthority(name, code)
                              ? { custodialAuthority: '' }
                              : {}),
                          }
                        : d,
                    );
                    clearErr('custodyStatusId');
                  }}
                />
                <FieldError message={errors.custodyStatusId} />
              </div>
              {showCustodialAuthority ? (
                <div className="ccr-fg">
                  <label>
                    Custodial Authority <span className="req">*</span>
                  </label>
                  <input
                    id="emla-custodialAuthority"
                    className={inpClass(Boolean(errors.custodialAuthority))}
                    value={draft.custodialAuthority}
                    readOnly={readOnly}
                    disabled={readOnly}
                    maxLength={MAX_REF}
                    onChange={(e) => {
                      setDraft((d) =>
                        d ? { ...d, custodialAuthority: sliceMax(e.target.value, MAX_REF) } : d,
                      );
                      clearErr('custodialAuthority');
                    }}
                  />
                  <FieldError message={errors.custodialAuthority} />
                </div>
              ) : null}
            </div>

            <div className="ccr-fg" style={{ marginTop: 14 }}>
              <label>Last Known Location</label>
              <textarea
                className={inpClass(false)}
                rows={3}
                maxLength={MAX_DESC}
                value={draft.lastKnownLocation}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) =>
                  setDraft((d) =>
                    d ? { ...d, lastKnownLocation: sliceMax(e.target.value, MAX_DESC) } : d,
                  )
                }
              />
            </div>
          </ReviewSubBlock>

          <TaskCaseUpdateSectionActions
            canSave={canSaveStep2}
            saving={savingStep === 2}
            readOnly={readOnly}
            onSave={() => void saveSection(2)}
            onCancel={beginCancel}
          />
        </ReviewSection>

        <ReviewSection step="Step 03" title="Legal & Extradition Case Details">
          <ReviewSubBlock title="Legal & Extradition Case Details">
            <div className="ccr-fg" style={{ marginBottom: 14 }}>
              <label>
                Arrest Warrant Available <span className="req">*</span>
              </label>
              <div className="ccr-radio-row" id="emla-arrestWarrant">
                {(['yes', 'no'] as const).map((v) => (
                  <label key={v} className="ccr-radio">
                    <input
                      type="radio"
                      name="emla-arrestWarrant"
                      checked={draft.arrestWarrantAvailable === v}
                      disabled={readOnly}
                      onChange={() => {
                        setDraft((d) =>
                          d
                            ? {
                                ...d,
                                arrestWarrantAvailable: v,
                                ...(v === 'no' ? { warrantReference: '' } : {}),
                              }
                            : d,
                        );
                        clearErr('arrestWarrantAvailable');
                        clearErr('warrantReference');
                      }}
                    />
                    {v === 'yes' ? 'Yes' : 'No'}
                  </label>
                ))}
              </div>
              <FieldError message={errors.arrestWarrantAvailable} />
            </div>

            {draft.arrestWarrantAvailable === 'yes' ? (
              <div className="ccr-fg" style={{ marginBottom: 14 }}>
                <label>
                  Warrant Reference <span className="req">*</span>
                </label>
                <input
                  id="emla-warrantReference"
                  className={inpClass(Boolean(errors.warrantReference))}
                  value={draft.warrantReference}
                  readOnly={readOnly}
                  disabled={readOnly}
                  maxLength={MAX_REF}
                  onChange={(e) => {
                    setDraft((d) =>
                      d ? { ...d, warrantReference: sliceMax(e.target.value, MAX_REF) } : d,
                    );
                    clearErr('warrantReference');
                  }}
                />
                <FieldError message={errors.warrantReference} />
              </div>
            ) : null}

            <div className="ccr-fg">
              <label>Applicable Treaty/Arrangement</label>
              <input
                className={inpClass(false)}
                value={draft.applicableTreatyArrangement}
                readOnly={readOnly}
                disabled={readOnly}
                maxLength={MAX_REF}
                onChange={(e) =>
                  setDraft((d) =>
                    d
                      ? { ...d, applicableTreatyArrangement: sliceMax(e.target.value, MAX_REF) }
                      : d,
                  )
                }
              />
            </div>
          </ReviewSubBlock>

          <TaskCaseUpdateSectionActions
            canSave={canSaveStep3}
            saving={savingStep === 3}
            readOnly={readOnly}
            onSave={() => void saveSection(3)}
            onCancel={beginCancel}
          />
        </ReviewSection>

        <ReviewSection step="Step 04" title="Timeline">
          <ReviewSubBlock title="Timeline">
            <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
              <div className="ccr-fg">
                <label>
                  Urgency Level <span className="req">*</span>
                </label>
                <WizardSelect
                  id="emla-urgency"
                  hasError={Boolean(errors.urgencyLevelId)}
                  value={draft.urgencyLevelId}
                  disabled={readOnly}
                  placeholder="Select Urgency Level"
                  options={toSelectOpts(lookups.urgencyLevels)}
                  onChange={(id) => {
                    const name = lookups.urgencyLevels.find((u) => u.id === id)?.name ?? '';
                    setDraft((d) => (d ? { ...d, urgencyLevelId: id, urgencyLevelName: name } : d));
                    if (!isHighUrgencyLevel(name)) clearErr('urgencyJustification');
                    clearErr('urgencyLevelId');
                  }}
                />
                <FieldError message={errors.urgencyLevelId} />
              </div>
              <div className="ccr-fg">
                <label>Deadline (if any)</label>
                <FormattedDateInput
                  id="emla-deadline"
                  className={inpClass(Boolean(errors.deadline))}
                  value={draft.deadline}
                  minDate={minDeadlineDate}
                  enableYmdViews
                  disabled={readOnly}
                  onChange={(next) => {
                    setDraft((d) => (d ? { ...d, deadline: next } : d));
                    clearErr('deadline');
                  }}
                />
                <FieldError message={errors.deadline} />
              </div>
            </div>
            <div className="ccr-fg">
              <label>
                Urgency Justification{' '}
                {urgencyJustificationRequired ? <span className="req">*</span> : null}
              </label>
              <textarea
                id="emla-urgencyJustification"
                className={inpClass(Boolean(errors.urgencyJustification))}
                rows={3}
                maxLength={MAX_TEXT}
                value={draft.urgencyJustification}
                readOnly={readOnly}
                disabled={readOnly}
                onChange={(e) => {
                  setDraft((d) =>
                    d ? { ...d, urgencyJustification: sliceMax(e.target.value, MAX_TEXT) } : d,
                  );
                  clearErr('urgencyJustification');
                }}
              />
              <FieldError message={errors.urgencyJustification} />
            </div>
          </ReviewSubBlock>

          <TaskCaseUpdateSectionActions
            canSave={canSaveStep4}
            saving={savingStep === 4}
            readOnly={readOnly}
            onSave={() => void saveSection(4)}
            onCancel={beginCancel}
          />
        </ReviewSection>

        <ReviewSection step="Step 05" title="Submission & Physical File Delivery">
          <ReviewSubBlock title="Request Submitter's Details">
            <ReviewGrid cols={4}>
              <ReviewField
                label="Submitted Division/ Unit"
                value={apiDisplayValue(draft.submittedDivisionName)}
              />
              <ReviewField
                label="Submitted by"
                value={apiDisplayValue(draft.submittedByDisplayName)}
              />
              <ReviewField
                label="Designation"
                value={apiDisplayValue(draft.submitterDesignation)}
              />
              <ReviewField label="Email Address" value={apiDisplayValue(draft.submitterEmail)} />
              <ReviewField
                label="Contact Number"
                value={apiDisplayValue(draft.submitterContactNo)}
              />
            </ReviewGrid>
          </ReviewSubBlock>
          <ReviewSubBlock title="Physical File Delivery">
            <ReviewField label="Delivery Method" value={deliveryLabel} fullWidth />
            {draft.deliveryOther.trim() ? (
              <ReviewField label="Other Delivery Method" value={draft.deliveryOther} />
            ) : null}
          </ReviewSubBlock>
        </ReviewSection>
      </div>
    </>
  );
}
